"use client";

import { useEffect, useRef } from "react";

/**
 * StarField — fundo de estrelas para Hero (VOR-64).
 *
 * Canvas leve (sem libs externas), com:
 * - ~140 estrelas em 3 camadas de profundidade (mais perto = maior + brilha mais)
 * - Twinkle leve via senoide com fase aleatória por estrela
 * - Pausa total quando aba fica oculta (pra economizar bateria)
 * - Respeita `prefers-reduced-motion` (twinkle desligado)
 *
 * Sem planetas (regra do card VOR-64): só pontos pequenos brancos.
 */
type Star = {
  x: number;
  y: number;
  r: number;
  baseAlpha: number;
  twinkle: number;
  phase: number;
};

export function StarField() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    let stars: Star[] = [];
    let raf = 0;
    let running = true;

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const resize = () => {
      const { offsetWidth: w, offsetHeight: h } = canvas;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      // VOR-75 (iter 2): densidade reduzida + opacidade base mais discreta.
      // Antes: ~1 estrela / 4kpx² + alpha base 0.35-0.90 (visualmente "ruidoso").
      // Agora: ~1 estrela / 9kpx² + alpha base 0.10-0.45 (estrelas distantes).
      const count = Math.max(50, Math.floor((w * h) / 9000));
      stars = Array.from({ length: count }, () => ({
        x: Math.random() * w,
        y: Math.random() * h,
        r: Math.random() * 1.2 + 0.25,
        baseAlpha: Math.random() * 0.35 + 0.1,
        twinkle: Math.random() * 0.25 + 0.05,
        phase: Math.random() * Math.PI * 2,
      }));
    };

    const draw = (t: number) => {
      if (!running) return;
      const { offsetWidth: w, offsetHeight: h } = canvas;
      ctx.clearRect(0, 0, w, h);

      for (const s of stars) {
        const alpha = reduced
          ? s.baseAlpha
          : s.baseAlpha + Math.sin(t / 1200 + s.phase) * s.twinkle;
        ctx.globalAlpha = Math.max(0, Math.min(1, alpha));
        ctx.fillStyle = "#FFFFFF";
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
      raf = requestAnimationFrame(draw);
    };

    const handleVisibility = () => {
      if (document.hidden) {
        running = false;
        cancelAnimationFrame(raf);
      } else if (!running) {
        running = true;
        raf = requestAnimationFrame(draw);
      }
    };

    resize();
    raf = requestAnimationFrame(draw);
    window.addEventListener("resize", resize);
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      running = false;
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 h-full w-full"
    />
  );
}
